import { useState } from 'react';
import { Flex, Form } from 'antd';
import TextField from '../form/text-field/text-field';
import styles from './security.module.scss';
import { Button } from '../button/button';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { passwordRules } from './security.utils';
import clsx from 'clsx';
import { InfoComponent } from '../info-component/info-component';
import { useTranslation } from 'react-i18next';

export type SecurityFieldType = {
  currentPassword: string;
  newPassword: string;
};

interface SecurityProps {
  onSubmit: (values: SecurityFieldType) => void;
  error?: string;
  isLoading?: boolean;
}

export function Security({ onSubmit, isLoading, error }: SecurityProps) {
  const { t } = useTranslation(['auth', 'common']);
  const [form] = Form.useForm();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [validations, setValidations] = useState(
    passwordRules.map((rule) => ({ ...rule, valid: false }))
  );
  const [formIsVisible, setIsFormVisible] = useState(false);

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPasswordValue = e.target.value;
    setNewPassword(newPasswordValue);
    setValidations(
      passwordRules.map((rule) => ({ ...rule, valid: rule.regex.test(newPasswordValue) }))
    );
  };

  const isSaveDisabled =
    !currentPassword || !newPassword || validations.some((validation) => !validation.valid);

  return (
    <>
      {!formIsVisible ? (
        <Flex vertical className={styles.securityContainer}>
          <Flex className={styles.securityContainerButton}>
            <p className={styles.passwordTitle}>{t('auth:fields.password')}</p>
            <Button buttonVariant="secondary" onClick={() => setIsFormVisible(true)}>
              {t('auth:profile.changePassword')}
            </Button>
          </Flex>

          <InfoComponent text={t('auth:profile.logoutNotice')} />
        </Flex>
      ) : (
        <Form
          form={form}
          layout="vertical"
          className={styles.formContainer}
          onFinish={(values) => {
            onSubmit(values);
          }}
        >
          <Form.Item<SecurityFieldType>
            label={t('auth:fields.currentPassword')}
            name="currentPassword"
            rules={[{ required: true, message: t('auth:validation.currentPasswordRequired') }]}
          >
            <TextField
              data-testid="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Form.Item>

          <Form.Item<SecurityFieldType>
            className={styles.newPasswordContainer}
            label={t('auth:fields.newPassword')}
            name="newPassword"
            rules={[{ required: true, message: t('auth:validation.newPasswordRequired') }]}
          >
            <TextField
              data-testid="newPassword"
              type="password"
              value={newPassword}
              onChange={handleNewPasswordChange}
            />
          </Form.Item>

          <div className={styles.passwordValidationContainer}>
            {validations.map(({ messageKey, valid }) => (
              <div
                key={messageKey}
                className={clsx(valid ? styles.validationItemValid : styles.validationItemInvalid)}
              >
                {valid ? (
                  <CheckCircleOutlined className={styles.validValidationIcon} />
                ) : (
                  <CloseCircleOutlined className={styles.invalidValidationIcon} />
                )}
                {t(`auth:${messageKey}`)}
              </div>
            ))}
          </div>

          <Form.Item>
            <Flex gap={10} className={styles.formButtons}>
              <Button
                className={styles.saveButton}
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={isSaveDisabled}
              >
                {t('common:actions.save')}
              </Button>
              <Button
                onClick={() => {
                  form.resetFields();
                  setValidations(passwordRules.map((rule) => ({ ...rule, valid: false })));
                  setIsFormVisible(false);
                }}
                buttonVariant="secondary"
                className={styles.cancelButton}
              >
                {t('common:actions.cancel')}
              </Button>
            </Flex>

            {error && <p className={styles.error}>{error}</p>}
          </Form.Item>
        </Form>
      )}
    </>
  );
}
